import { useGetDayCountConventionsQuery } from '@src/components/OTC/externalRefDataApi';
import { PERIODIC_PERIOD_VALUES, type PeriodFrequency } from '@src/types/OTC/loanSchedulePeriod';
import { LoanPaymentTypeEnum, LoanQuoteTerminationTypeEnum, PrepaymentOptionEnum } from '@talos/kyoko';
import { useMemo } from 'react';

export type LoanPaymentPeriodOption = { displayName: string; value: PeriodFrequency };
export type LoanPaymentTypeOption = { displayName: string; value: LoanPaymentTypeEnum };
export type LoanTermTypeOption = { displayName: string; value: LoanQuoteTerminationTypeEnum };
export type LoanPaymentCurrencyOption = { value: string; displayName: string };

const PERIODIC_PERIOD_LABELS: Record<PeriodFrequency, string> = {
  '1D': 'Daily',
  '1W': 'Weekly',
  '1M': 'Monthly',
};

function toPeriodicPeriodOptions(): Array<LoanPaymentPeriodOption> {
  return PERIODIC_PERIOD_VALUES.map(value => ({
    displayName: PERIODIC_PERIOD_LABELS[value],
    value,
  }));
}

/** Payment period toggle options (Periodic). Independent list; values from PERIODIC_PERIOD_VALUES. */
export const LOAN_PAYMENT_PERIOD_OPTIONS = toPeriodicPeriodOptions();

/** Reset period toggle options (floating rate). Independent list; values from PERIODIC_PERIOD_VALUES. */
export const LOAN_RESET_PERIOD_OPTIONS = toPeriodicPeriodOptions();

export const LOAN_PAYMENT_TYPE_OPTIONS = [
  { displayName: 'Periodic', value: LoanPaymentTypeEnum.Periodic },
  { displayName: 'Bullet', value: LoanPaymentTypeEnum.Bullet },
] as const satisfies Array<LoanPaymentTypeOption>;

export const LOAN_TERM_TYPE_OPTIONS = [
  { displayName: 'Fixed', value: LoanQuoteTerminationTypeEnum.Fixed },
  { displayName: 'Open', value: LoanQuoteTerminationTypeEnum.OpenTerm },
] as const satisfies Array<LoanTermTypeOption>;

export type DayCountConventionOption = { displayName: string; value: string };
export type RateSourceOption = { displayName: string; value: string; description?: string };
export type PrepaymentOptionOption = { displayName: string; value: PrepaymentOptionEnum };

/**
 * Hook to retrieve day count convention options from the Reference Data Service
 * Spec: "Drop-down to be provided by the Reference Data Service: 30/360, Actual/360, Actual/365, Actual/Actual"
 */
export const useDayCountConventionOptions = (): DayCountConventionOption[] => {
  const { data: dayCountConventions } = useGetDayCountConventionsQuery();

  return useMemo(() => {
    return (
      dayCountConventions?.map(convention => ({
        displayName: convention.DisplayName,
        value: convention.Code,
      })) ?? []
    );
  }, [dayCountConventions]);
};

export const PREPAYMENT_OPTION_OPTIONS: PrepaymentOptionOption[] = [
  { displayName: 'Full', value: PrepaymentOptionEnum.Full },
  { displayName: 'Partial / Full', value: PrepaymentOptionEnum.Partial },
  { displayName: 'None', value: PrepaymentOptionEnum.None },
];

// Pure label getter functions
export const getLoanPaymentCurrencyLabel = (option: LoanPaymentCurrencyOption): string => option.displayName;
export const getTermTypeLabel = (option: LoanTermTypeOption): string => option.displayName;
export const getPaymentPeriodLabel = (option: LoanPaymentPeriodOption): string => option.displayName;
export const getPaymentTypeLabel = (option: LoanPaymentTypeOption): string => option.displayName;
export const getDayCountConventionLabel = (option: DayCountConventionOption): string => option.displayName;
export const getRateSourceLabel = (option: RateSourceOption): string => option.displayName;
export const getPrepaymentOptionLabel = (option: PrepaymentOptionOption): string => option.displayName;

// MVP-only restriction
export type LoanCollateralAssetOption = { value: string; label: string };
